import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowRight, CheckCircle, ShieldCheck, WhatsappLogo, EnvelopeSimple, Phone,
  MapPin, PaperPlaneTilt, FacebookLogo, TiktokLogo, YoutubeLogo, InstagramLogo,
} from "@phosphor-icons/react";

// 👉 Coordonnées éditables du groupe YAYA
const CONTACT = {
  whatsapp: "237659946824",
  phoneDisplay: "+237 6 59 94 68 24",
  email: "contact@yayaexam.cm",
  city: "Yaoundé / Douala, Cameroun",
  // Laissez vide pour masquer le réseau ; ajoutez l'URL complète pour l'activer.
  socials: [
    { key: "facebook", label: "Facebook", icon: FacebookLogo, url: "", color: "#1877F2" },
    { key: "tiktok", label: "TikTok", icon: TiktokLogo, url: "", color: "#010101" },
    { key: "youtube", label: "YouTube", icon: YoutubeLogo, url: "", color: "#FF0000" },
    { key: "instagram", label: "Instagram", icon: InstagramLogo, url: "", color: "#E1306C" },
  ],
};

function PublicHeader() {
  return (
    <>
      <div className="bg-forest px-4 py-2 text-center text-xs font-medium text-white sm:text-sm">
        <CheckCircle weight="fill" className="mr-1.5 inline text-gold" size={15} />
        100% conforme aux programmes officiels du Cameroun — MINESEC & MINEDUB
      </div>
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" data-testid="contact-logo"><Logo /></Link>
          <nav className="hidden items-center gap-8 text-sm font-semibold text-ink/70 md:flex">
            <Link to="/" className="hover:text-terracotta">Accueil</Link>
            <Link to="/a-propos" className="hover:text-terracotta">À propos</Link>
            <a href="/#tarifs" className="hover:text-terracotta">Tarifs</a>
            <Link to="/contact" className="text-terracotta">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/connexion" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-ink transition hover:bg-sand sm:inline-block">Connexion</Link>
            <Link to="/connexion?mode=register" className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_22px_rgba(255,75,10,0.35)] transition hover:-translate-y-0.5 hover:bg-[#E03E00]">Commencer</Link>
          </div>
        </div>
      </header>
    </>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-border bg-white py-10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row lg:px-8">
        <Logo />
        <p className="text-sm text-slate-500">© 2026 YAYA EXAM ACADEMY — Réussir aujourd'hui. Construire demain.</p>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <Link to="/a-propos" className="hover:text-terracotta">À propos</Link>
          <Link to="/contact" className="hover:text-terracotta">Contact</Link>
        </div>
      </div>
    </footer>
  );
}

const CHANNELS = [
  { icon: WhatsappLogo, tone: "#25D366", title: "WhatsApp", value: CONTACT.phoneDisplay,
    href: `https://wa.me/${CONTACT.whatsapp}`, cta: "Discuter maintenant", testid: "contact-whatsapp" },
  { icon: EnvelopeSimple, tone: "#FF4B0A", title: "E-mail", value: CONTACT.email,
    href: `mailto:${CONTACT.email}`, cta: "Nous écrire", testid: "contact-email" },
  { icon: Phone, tone: "#05357E", title: "Téléphone", value: CONTACT.phoneDisplay,
    href: `tel:+${CONTACT.whatsapp}`, cta: "Appeler", testid: "contact-phone" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const activeSocials = CONTACT.socials.filter((s) => s.url);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Veuillez indiquer votre nom et votre message.");
      return;
    }
    const text =
      `Bonjour YAYA EXAM ACADEMY,\n\n` +
      `Nom : ${form.name}\n` +
      (form.email ? `E-mail : ${form.email}\n` : "") +
      `\n${form.message}`;
    const waUrl = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(text)}`;
    // Ouvrir WhatsApp de façon SYNCHRONE dans le geste utilisateur (sinon bloqué en pop-up sur mobile/Safari).
    const win = window.open(waUrl, "_blank");
    if (!win) window.location.href = waUrl;
    // Enregistrer le message en arrière-plan (au mieux) pour le back-office admin.
    api.post("/contact", { name: form.name, email: form.email || null, message: form.message }).catch(() => {});
    toast.success("Ouverture de WhatsApp… envoyez votre message pour finaliser.");
    setForm({ name: "", email: "", message: "" });
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <div className="min-h-screen bg-white text-ink">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-cream">
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-60" />
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-terracotta">
            <PaperPlaneTilt weight="fill" size={14} /> Contactez-nous
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">On est là pour vous aider</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Une question sur un abonnement, un partenariat école, une aide technique ? L'équipe YAYA vous répond
            rapidement — le plus simple reste WhatsApp.
          </p>
        </div>
      </section>

      {/* Channels */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              return (
                <a key={c.title} href={c.href} target="_blank" rel="noreferrer" data-testid={c.testid}
                  className="group rounded-3xl border border-border bg-white p-7 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl transition group-hover:scale-110"
                    style={{ background: `${c.tone}15`, color: c.tone }}>
                    <Icon size={30} weight="fill" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-forest">{c.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.value}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-terracotta">
                    {c.cta} <ArrowRight weight="bold" size={16} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </a>
              );
            })}
          </div>

          {/* Info + Form */}
          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <div className="rounded-3xl bg-forest p-8 text-white">
              <h2 className="font-display text-2xl font-extrabold">Coordonnées</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-center gap-3"><WhatsappLogo weight="fill" className="text-gold" size={20} /> {CONTACT.phoneDisplay}</li>
                <li className="flex items-center gap-3"><EnvelopeSimple weight="fill" className="text-gold" size={20} /> {CONTACT.email}</li>
                <li className="flex items-center gap-3"><MapPin weight="fill" className="text-gold" size={20} /> {CONTACT.city}</li>
              </ul>
              <div className="mt-8">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">Nos réseaux</div>
                {activeSocials.length ? (
                  <div className="mt-3 flex gap-3">
                    {activeSocials.map((s) => {
                      const Icon = s.icon;
                      return (
                        <a key={s.key} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label}
                          data-testid={`social-${s.key}`}
                          className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 hover:-translate-y-0.5">
                          <Icon size={20} weight="fill" />
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-3">
                    {CONTACT.socials.map((s) => {
                      const Icon = s.icon;
                      return (
                        <span key={s.key} title="Bientôt disponible" aria-label={s.label}
                          className="grid h-11 w-11 cursor-default place-items-center rounded-full bg-white/10 text-white/60">
                          <Icon size={20} weight="fill" />
                        </span>
                      );
                    })}
                  </div>
                )}
                {!activeSocials.length && (
                  <p className="mt-3 text-xs text-white/60">Nos pages officielles arrivent bientôt — écrivez-nous en attendant.</p>
                )}
              </div>
            </div>

            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-border bg-white p-8 shadow-sm">
              <h2 className="font-display text-2xl font-extrabold text-forest">Envoyez-nous un message</h2>
              <p className="mt-1 text-sm text-slate-500">Votre message s'ouvrira dans WhatsApp, pré-rempli.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-forest">Votre nom</label>
                  <input {...field("name")} data-testid="contact-name" placeholder="Ex. Aïcha N."
                    className="w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-terracotta" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-forest">Votre e-mail (facultatif)</label>
                  <input {...field("email")} type="email" data-testid="contact-form-email" placeholder="vous@exemple.com"
                    className="w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-terracotta" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-forest">Votre message</label>
                  <textarea {...field("message")} data-testid="contact-message" rows={4} placeholder="Comment pouvons-nous vous aider ?"
                    className="w-full resize-none rounded-xl border-2 border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-terracotta" />
                </div>
                <button type="submit" data-testid="contact-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:brightness-105">
                  <WhatsappLogo weight="fill" /> Envoyer via WhatsApp
                </button>
              </div>
            </form>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2"><ShieldCheck weight="fill" className="text-forest" /> Réponse rapide, 7j/7</span>
            <span className="inline-flex items-center gap-2"><CheckCircle weight="fill" className="text-terracotta" /> Support en français</span>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
